/**
 * 두 전사의 차이 분류 (TRANSCRIPT_PROTOCOL §9).
 *   Level 0 의미 동일 · 1 표현 차이 · 2 내용 일부 차이 · 3 임상적으로 중요한 차이 · 4 위험 관련 차이
 * 의미 없는 차이는 자동 통과시키고, 공식 기록에 영향을 줄 수 있는 것만 확인 대상으로 남긴다.
 */
import { charSimilarity, contentTokens, isFiller, lcs } from './normalize';

export type DiscrepancyCategory =
  | 'wording'
  | 'negation'
  | 'number'
  | 'person'
  | 'medication'
  | 'diagnosis'
  | 'risk'
  | 'event'
  | 'terminology'
  | 'unknown';

export type Classified = { level: 0 | 1 | 2 | 3 | 4; category: DiscrepancyCategory; similarity: number };

const RISK = /(죽|자살|자해|사라지|해치|때리|때렸|맞았|학대|목숨|극단|칼|베었|뛰어내|술을|술 마|수면제|약을 모|폭력)/;
const NEGATION = /(없|않|안\s|안했|못|아니|말아|마세|모르)/g;
const MEDICATION = /(약|수면제|항우울|안정제|처방|복용|알씩|알만)/;
const DIAGNOSIS = /(우울증|공황|불안장애|진단|정신과|ADHD|조울|양극성|강박|PTSD|트라우마)/;
const PERSON =
  /(엄마|어머니|아버지|아빠|동생|형|누나|언니|오빠|남편|아내|배우자|팀장|선배|후배|친구|교수|목사|전도사|선생|할머니|할아버지|딸|아들|애인|연인)/;
const NUM_WORD =
  /(\d+|한|두|세|네|다섯|여섯|일곱|여덟|아홉|열|스무|반)\s?(번|알|시간|일|주|개월|달|년|살|명|잔|병|원|만원|시|분|회|차례)/g;

const count = (re: RegExp, s: string) => (s.match(new RegExp(re.source, 'g')) ?? []).length;
const numbers = (s: string) =>
  (s.match(NUM_WORD) ?? [])
    .map((x) => x.replace(/\s/g, ''))
    .sort()
    .join('|');

/** 서로 다른 내용 토큰 (LCS 기준으로 맞춰지지 않은 토큰) */
function diffTokens(a: string[], b: string[]): { onlyA: string[]; onlyB: string[] } {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--)
    for (let j = n - 1; j >= 0; j--)
      dp[i]![j] = a[i] === b[j] ? dp[i + 1]![j + 1]! + 1 : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
  const onlyA: string[] = [];
  const onlyB: string[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      i++;
      j++;
    } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) onlyA.push(a[i++]!);
    else onlyB.push(b[j++]!);
  }
  while (i < m) onlyA.push(a[i++]!);
  while (j < n) onlyB.push(b[j++]!);
  return { onlyA, onlyB };
}

export function classify(galaxyText: string, iphoneText: string, glossaryTerms: string[] = []): Classified {
  const a = contentTokens(galaxyText).map((t) => t.norm);
  const b = contentTokens(iphoneText).map((t) => t.norm);
  const sim = a.length + b.length ? (2 * lcs(a, b)) / (a.length + b.length) : 1;
  const { onlyA, onlyB } = diffTokens(a, b);
  const sig = [...onlyA, ...onlyB].filter((t) => !isFiller(t));
  if (sig.length === 0) return { level: onlyA.length + onlyB.length ? 1 : 0, category: 'wording', similarity: sim };

  const diffText = sig.join(' ');
  if (RISK.test(diffText) || RISK.test(galaxyText) !== RISK.test(iphoneText)) {
    return { level: 4, category: 'risk', similarity: sim };
  }
  if (count(NEGATION, galaxyText) !== count(NEGATION, iphoneText)) {
    return { level: RISK.test(galaxyText + iphoneText) ? 4 : 3, category: 'negation', similarity: sim };
  }
  if (numbers(galaxyText) !== numbers(iphoneText)) return { level: 3, category: 'number', similarity: sim };
  if (MEDICATION.test(diffText)) return { level: 3, category: 'medication', similarity: sim };
  if (DIAGNOSIS.test(diffText)) return { level: 3, category: 'diagnosis', similarity: sim };
  if (PERSON.test(diffText)) return { level: 3, category: 'person', similarity: sim };
  // 용어 사전에 있는 말이 한쪽에만 있거나 비슷한 소리로 바뀐 경우
  for (const term of glossaryTerms) {
    const tn = contentTokens(term)
      .map((t) => t.norm)
      .join('');
    if (sig.some((t) => t === tn || charSimilarity(t, tn) >= 0.5))
      return { level: 2, category: 'terminology', similarity: sim };
  }
  // 조사·어미 수준의 짧은 차이
  if (sig.every((t) => t.length <= 1) || sim >= 0.9) return { level: 1, category: 'wording', similarity: sim };
  return { level: 2, category: sim < 0.5 ? 'event' : 'wording', similarity: sim };
}

/**
 * 용어 사전 보정: Galaxy가 비슷한 소리로 잘못 적었고 iPhone이 사전 용어를 적었으면 사전 용어로 바꾼다.
 * 원문(RAW)은 그대로이고 Working Transcript에만 적용된다.
 */
export function applyGlossary(
  galaxyText: string,
  iphoneText: string,
  glossaryTerms: string[],
): { text: string; corrected: string[] } {
  let text = galaxyText;
  const corrected: string[] = [];
  for (const term of glossaryTerms) {
    if (!iphoneText.includes(term) || galaxyText.includes(term)) continue;
    const words = term.split(/\s+/).length;
    const toks = galaxyText.split(/\s+/);
    for (let i = 0; i + words <= toks.length; i++) {
      const cand = toks.slice(i, i + words).join(' ');
      const core = cand.replace(/[^가-힣A-Za-z0-9 ]/g, '');
      // 조사 부분은 남기고 앞부분만 비교
      const head = core.slice(0, term.length);
      if (charSimilarity(head, term) >= 0.5 && head !== term) {
        text = text.replace(head, term);
        corrected.push(term);
        break;
      }
    }
  }
  return { text, corrected };
}
